import { Helmet } from 'react-helmet-async';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e", card: "#13131a" };

const S = {
  h2: { color: COLORS.text, fontSize: '1.4rem', fontWeight: 800, margin: '40px 0 12px', letterSpacing: '-0.3px' },
  h3: { color: COLORS.text, fontSize: '1.1rem', fontWeight: 700, margin: '28px 0 8px' },
  p: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 20 },
  strong: { color: COLORS.text },
  li: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 10 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 28 },
  th: { color: COLORS.accent, fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', padding: '10px 14px', borderBottom: `2px solid ${COLORS.border}`, textAlign: 'left' },
  td: { color: COLORS.muted, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, lineHeight: 1.5 },
  tdFirst: { color: COLORS.text, fontWeight: 600, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}` },
  cta: { display: 'block', margin: '40px 0', padding: '18px 32px', background: COLORS.accent, color: '#000', fontWeight: 800, fontSize: '1rem', borderRadius: 10, textDecoration: 'none', textAlign: 'center', letterSpacing: '-0.2px' },
  callout: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${COLORS.accent}`, borderRadius: 8, padding: '16px 20px', marginBottom: 24 },
};

const faqItems = [
  {
    q: 'Is debt consolidation a good idea?',
    a: 'It can be — if the new rate is meaningfully lower than your current rates and you commit to not adding new debt. Consolidating $15,000 in credit cards at 22% into a personal loan at 13% saves roughly $4,500 in interest on a 4-year payoff. But if you run up the cards again after consolidating, you end up worse off.',
  },
  {
    q: 'Does debt consolidation hurt your credit score?',
    a: 'In the short term, applying for a new loan causes a small hard inquiry dip. But paying off multiple cards reduces your credit utilization, which typically boosts your score within 1–3 months. Long-term, consolidation usually helps your credit if you don\'t add new debt.',
  },
  {
    q: 'What credit score do I need to consolidate debt?',
    a: 'Most personal loan lenders require a score of 620+ for approval, though rates improve significantly at 700+. Balance transfer cards usually require 680+ for 0% promotional offers. If your score is lower, a credit union or secured loan may be an alternative.',
  },
  {
    q: 'What is the difference between debt consolidation and debt settlement?',
    a: 'Consolidation combines debts into a new loan — you pay back the full amount, often at a lower rate. Settlement negotiates with creditors to accept less than you owe, which means debt is forgiven. Settlement severely damages your credit score and may have tax consequences. They are not the same.',
  },
  {
    q: 'Can I consolidate student loans and credit cards together?',
    a: 'Federal student loans should generally be kept separate — consolidating them into a private loan loses federal protections (income-driven repayment, forgiveness programs). Credit cards and private loans can often be consolidated together with a personal loan.',
  },
];

export default function DebtConsolidationProsCons() {
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <html lang="en" />
        <title>Debt Consolidation: Pros and Cons — Is It Worth It? | ClearMyDebt</title>
        <meta name="description" content="Debt consolidation pros and cons explained clearly — with real numbers on when it saves money and when it doesn't. Free payoff calculator included." />
        <link rel="canonical" href="https://clearmydebt.app/blog/debt-consolidation-pros-cons/" />
        <meta property="og:title" content="Debt Consolidation: Pros and Cons — Is It Worth It? | ClearMyDebt" />
        <meta property="og:description" content="Debt consolidation pros and cons explained with real numbers — when it saves money and when it makes things worse." />
        <meta property="og:url" content="https://clearmydebt.app/blog/debt-consolidation-pros-cons/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/blog/debt-consolidation-pros-cons/" />
        <link rel="alternate" hreflang="en" href="https://clearmydebt.app/blog/debt-consolidation-pros-cons/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "headline": "Debt Consolidation: Pros and Cons — Is It Worth It?",
              "description": "Debt consolidation pros and cons explained clearly — with real numbers on when it saves money and when it doesn't.",
              "url": "https://clearmydebt.app/blog/debt-consolidation-pros-cons/",
              "publisher": { "@type": "Organization", "name": "ClearMyDebt", "url": "https://clearmydebt.app" },
            },
            {
              "@type": "FAQPage",
              "mainEntity": faqItems.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
          ],
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px 80px' }}>

        <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: 32 }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
          <span style={{ margin: '0 8px' }}>&#8250;</span>
          <span>Blog</span>
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' }}>
            Debt Strategy
          </div>
          <h1 style={{ fontSize: 'clamp(26px,5vw,40px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 16px', lineHeight: 1.15 }}>
            Debt Consolidation: Pros and Cons
          </h1>
          <p style={{ color: COLORS.muted, fontSize: '1.1rem', lineHeight: 1.6, margin: 0 }}>
            When consolidation genuinely saves money &#8212; and when it just moves the problem around. Real numbers, no hype.
          </p>
        </div>

        <p style={S.p}>
          Debt consolidation sounds simple: combine multiple debts into one loan with one payment.
          But it is only useful if the new rate is lower than what you are currently paying. Otherwise,
          you are rearranging chairs on a ship that is still sinking.
        </p>
        <p style={S.p}>
          This article breaks down exactly when consolidation works, the real costs and risks,
          and how to run the numbers for your situation.
        </p>

        <a href="/personal-loan-payoff-calculator/" style={S.cta}>Calculate Your Consolidated Loan Payoff &#8212; Free</a>

        <h2 style={S.h2}>What Is Debt Consolidation?</h2>
        <p style={S.p}>
          Debt consolidation means taking out a new loan &#8212; usually a personal loan or balance transfer
          credit card &#8212; to pay off multiple existing debts. You then make one payment to the new lender
          instead of several to different creditors.
        </p>
        <p style={S.p}>
          The most common forms:
        </p>
        <ul style={{ paddingLeft: 24, marginBottom: 24 }}>
          <li style={S.li}><strong style={S.strong}>Personal loan:</strong> Fixed rate, fixed term, often 3&#8211;7 years. Good for paying off credit cards.</li>
          <li style={S.li}><strong style={S.strong}>0% balance transfer card:</strong> Temporarily removes interest for 12&#8211;21 months. Best if you can pay off during the promo period.</li>
          <li style={S.li}><strong style={S.strong}>Home equity loan / HELOC:</strong> Uses your home as collateral. Very low rates but high risk &#8212; defaulting can cost you your home.</li>
          <li style={S.li}><strong style={S.strong}>Debt management plan (DMP):</strong> Arranged through a non-profit credit counsellor. Not technically a new loan &#8212; the agency negotiates lower rates and consolidates your payments.</li>
        </ul>

        <h2 style={S.h2}>The Pros</h2>

        <h3 style={S.h3}>1. Lower interest rate saves real money</h3>
        <p style={S.p}>
          This is the only reason consolidation makes financial sense. If you can replace 22% credit
          card debt with a 13% personal loan, you save 9 percentage points on every dollar of balance.
        </p>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Balance</th>
              <th style={S.th}>At 22% (4yr payoff)</th>
              <th style={S.th}>At 13% (4yr payoff)</th>
              <th style={S.th}>Savings</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>$8,000</td>
              <td style={S.td}>$4,200 interest</td>
              <td style={S.td}>$2,300 interest</td>
              <td style={S.td}>$1,900</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$15,000</td>
              <td style={S.td}>$8,000 interest</td>
              <td style={S.td}>$4,400 interest</td>
              <td style={S.td}>$3,600</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$25,000</td>
              <td style={S.td}>$13,500 interest</td>
              <td style={S.td}>$7,300 interest</td>
              <td style={S.td}>$6,200</td>
            </tr>
          </tbody>
        </table>

        <h3 style={S.h3}>2. Simplified repayment</h3>
        <p style={S.p}>
          Managing five different credit card due dates, rates, and minimum calculations is cognitively
          expensive. One payment to one lender eliminates that complexity &#8212; and reduces the chance
          of a missed payment.
        </p>

        <h3 style={S.h3}>3. Fixed payoff date</h3>
        <p style={S.p}>
          Personal loans have a set term. Unlike credit cards where the minimum payment shrinks
          as the balance falls (keeping you in debt longer), a personal loan gives you a concrete
          payoff date from day one.
        </p>

        <h3 style={S.h3}>4. Possible credit score boost</h3>
        <p style={S.p}>
          Paying off credit card balances lowers your credit utilization ratio &#8212; one of the
          biggest factors in your score. If you keep the cards open and don&#8217;t run them up again,
          your score can improve significantly within a few months.
        </p>

        <h2 style={S.h2}>The Cons</h2>

        <h3 style={S.h3}>1. You still owe the same amount</h3>
        <p style={S.p}>
          Consolidation does not reduce what you owe &#8212; it only restructures it. The danger is
          treating a zero-balance credit card as &#8220;free money&#8221; after consolidating. This is how
          people end up with both a consolidation loan and new card debt, effectively doubling
          their problem.
        </p>

        <div style={S.callout}>
          <strong style={S.strong}>The most common mistake:</strong>{" "}
          <span style={{ color: COLORS.muted }}>Consolidating $12,000 in credit card debt into a personal loan, then spending $8,000 back on the cards within 18 months. You now owe $20,000 instead of $12,000.</span>
        </div>

        <h3 style={S.h3}>2. Fees and origination costs</h3>
        <p style={S.p}>
          Personal loans often charge origination fees of 1&#8211;8% of the loan amount, deducted
          upfront. A $15,000 loan with a 5% origination fee means you receive $14,250 but owe
          $15,000. Balance transfer cards charge 3&#8211;5% of the transferred amount. These costs
          reduce &#8212; and can eliminate &#8212; the savings.
        </p>

        <h3 style={S.h3}>3. Longer loan terms can cost more in total</h3>
        <p style={S.p}>
          Consolidating to reduce the monthly payment often requires extending the loan term.
          A lower rate over 7 years can cost more in total interest than the original 3-year payoff
          of higher-rate cards. Always compare total interest paid, not just monthly payment.
        </p>

        <h3 style={S.h3}>4. Your home is at risk with a HELOC</h3>
        <p style={S.p}>
          Home equity loans and lines of credit offer the lowest rates &#8212; but they convert
          unsecured credit card debt into secured debt backed by your home. If you default, you
          can lose your house. This is rarely the right tool for credit card consolidation.
        </p>

        <h2 style={S.h2}>When Consolidation Makes Sense</h2>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Situation</th>
              <th style={S.th}>Consolidation?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>New rate is 5+ points lower</td>
              <td style={S.td}>Yes — strong case</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>You can pay off in 0% promo period</td>
              <td style={S.td}>Yes — balance transfer wins</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>New rate is only 1–2 points lower</td>
              <td style={S.td}>Maybe — run the total cost numbers</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Rate is similar but term is longer</td>
              <td style={S.td}>No — likely costs more overall</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>You are likely to use cards again</td>
              <td style={S.td}>No — fix spending first</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>Using your home as collateral</td>
              <td style={S.td}>Rarely — too much risk</td>
            </tr>
          </tbody>
        </table>

        <h2 style={S.h2}>Frequently Asked Questions</h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: COLORS.card, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

        <div style={{ ...S.callout, marginTop: 40 }}>
          <p style={{ color: COLORS.text, fontWeight: 700, fontSize: '1.05rem', margin: '0 0 8px' }}>
            Run the numbers before you consolidate
          </p>
          <p style={{ color: COLORS.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
            Enter your current debts into the free calculator to see your total interest on the current
            path &#8212; then compare it to what a consolidated loan at a lower rate would cost.
          </p>
          <a href="/" style={{ ...S.cta, margin: 0 }}>Calculate and Compare &#8212; Free</a>
        </div>

      </div>
    </div>
  );
}
